import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AxiosError } from 'axios';
import { checkAuthStatus, loginUser, setAuthToken, checkOrgStatus, getProjects, getNotifications } from '../services/api';
import { useTheme } from '../context/ThemeContext';
import { ThemeToggle } from '../components/ui/ThemeToggle';
import { OAuthButton } from '../components/auth/OAuthButton';
import { CircularProgress, Box, Typography, Backdrop } from '@mui/material';
import { log } from 'utils/logger';
import { authService } from '../services/authService';

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { theme, toggleTheme } = useTheme();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showWorkspaceLoading, setShowWorkspaceLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showResendVerification, setShowResendVerification] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);

  // Handle state from registration redirect and URL params
  useEffect(() => {
    const state = location.state as any;
    const urlParams = new URLSearchParams(location.search);
    
    if (state?.email) {
      setEmail(state.email);
    }
    if (state?.message) {
      setSuccessMessage(state.message);
    }
    
    // Handle auto-login after email verification
    const autoLogin = urlParams.get('autoLogin');
    const autoLoginEmail = localStorage.getItem('autoLoginEmail');
    const autoLoginAfterVerification = localStorage.getItem('autoLoginAfterVerification');
    
    if (autoLogin === 'true' && autoLoginEmail && autoLoginAfterVerification === 'true') {
      // Clear auto-login flags
      localStorage.removeItem('autoLoginEmail');
      localStorage.removeItem('autoLoginAfterVerification');
      
      // Set email and show message
      setEmail(autoLoginEmail);
      setSuccessMessage('Email verified successfully! Please enter your password to access your dashboard.');
      
      // Clear URL parameters
      window.history.replaceState({}, document.title, window.location.pathname);
    }
    
    // Clear any verification parameters from URL (shouldn't be needed now)
    if (urlParams.get('verified') === 'true') {
      // Clear the URL parameters
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [location.state, location.search]);

  const handleResendVerification = async () => {
    if (!email) {
      setError('Please enter your email address');
      return;
    }

    setResendLoading(true);
    try {
      await authService.resendVerification(email);
      setSuccessMessage('Verification email sent! Please check your inbox.');
      setError('');
      setShowResendVerification(false);
    } catch (error: any) {
      setError(error.message || 'Failed to resend verification email');
    } finally {
      setResendLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    setShowResendVerification(false);
    
    try {
      log("Login: Starting login process");
      // Attempt login
      const loginResult = await loginUser({ email, password });
      log("Login: Received access token:", loginResult.accessToken);

      // Store tokens and set axios header
      setAuthToken(loginResult.accessToken);
      log("Login: Tokens stored in localStorage");

      // Validate token before navigation
      log("Login: Validating token with checkAuthStatus");
      const authCheck = await checkAuthStatus();
      log("Login: Auth check result:", authCheck ? "Success" : "Failed");
      
      if (authCheck) {
        // Store encrypted user data in localStorage
        log("Login: Storing user data with encryption");
        // Use await to ensure user data is stored before proceeding
        await import('../utils/encryption').then(({ setUserData }) => {
          setUserData(authCheck);
          log("Login: User data stored successfully");
        });

        // Get projects and check org status if no projects exist
        log("Login: Getting projects");
        const projects = await getProjects();
        log("Login: Projects received:", projects.length);
        if (projects.length === 0) {
          setShowWorkspaceLoading(true);
          // Set a flag to indicate workspace creation
          localStorage.setItem('workspaceCreated', 'true');
          await checkOrgStatus();
          getNotifications()
          setShowWorkspaceLoading(false);
          // Navigate to dashboard with a query parameter
          navigate('/dashboard?newWorkspace=true');
        } else {
          // Only navigate if token check succeeds
          getNotifications()
          navigate('/dashboard');
          window.dispatchEvent(new Event('storage'));

        }
      } else {
        throw new Error('Token validation failed');
      }
    } catch (error) {
      const axiosError = error as AxiosError<{ detail: string }>;
      const errorMessage = axiosError.response?.data?.detail || 'An error occurred during login.';
      
      // Check if the error is the specific email verification message from backend
      if (errorMessage === "Email not verified. Please check your email and verify your account before logging in.") {
        setError(errorMessage);
        setShowResendVerification(true);
      } else if (errorMessage.toLowerCase().includes('email') && errorMessage.toLowerCase().includes('verify')) {
        // Fallback for other email verification related errors
        setError('Please verify your email address before logging in. Check your inbox for the verification email.');
        setShowResendVerification(true);
      } else {
        setError(errorMessage);
        setShowResendVerification(false);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Backdrop
        open={showWorkspaceLoading}
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: theme === 'dark' ? '#000000' : '#fff',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 10
        }}
      >
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 3
        }}>
          <img
            src="/images/ana_logo.png"
            alt="ANA Logo"
            style={{ height: '48px', width: 'auto' }}
          />
          <img
            src={theme === 'dark' ? "/images/ana_text_dark.svg" : "/images/ana_text.svg"}
            alt="Ana"
            style={{ height: '36px', width: 'auto' }}
          />
        </Box>
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 4
        }}>
          <CircularProgress
            size={28}
            sx={{
              color: '#FF6B00',
              '& .MuiCircularProgress-circle': {
                strokeWidth: 4
              }
            }}
          />
          <Typography
            sx={{
              color: theme === 'dark' ? '#f1f5f9' : '#1A1A1A',
              fontSize: '18px',
              fontWeight: 500,
              letterSpacing: '-0.01em'
            }}
          >
            Creating your workspace...
          </Typography>
        </Box>
      </Backdrop>

      <div className="w-full min-h-screen bg-background">
        <div className="absolute top-4 right-4">
          <ThemeToggle theme={theme} onToggle={toggleTheme} />
        </div>
        <div className="w-full min-h-screen flex flex-col items-center justify-center">
          <div className="w-[440px] space-y-6">
            <div className="flex flex-col items-center justify-center text-center">
              <img src="/images/ana_logo.png" alt="ANA Logo" className="h-12 w-12" />
              <img
                src="/images/ana_text.svg"
                alt="ANA"
                className="mt-4 h-8 w-auto dark:invert dark:brightness-200"
              />
              <h1 className="mt-6 text-2xl font-bold text-foreground">Security App</h1>
              <p className="mt-2 text-sm text-muted-foreground">Sign in to your account</p>
            </div>

            {successMessage && (
              <div className="p-4 bg-green-500/10 border border-green-500/20 rounded-lg text-green-600 dark:text-green-400 text-sm">
                {successMessage}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-3">
              <div>
                <label className="block text-sm text-muted-foreground mb-1">Email address</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={loading}
                  className="w-full px-3 py-2 bg-input border border-border rounded-md text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:border-[#ff4800] disabled:opacity-50"
                  placeholder="Enter your email address"
                />
              </div>

              <div>
                <label className="block text-sm text-muted-foreground mb-1">Password</label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    disabled={loading}
                    className="w-full px-3 py-2 bg-input border border-border rounded-md text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:border-[#ff4800] disabled:opacity-50"
                    placeholder="Enter your password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground focus:outline-none"
                  >
                    {showPassword ? (
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              <div className="text-right">
                <a 
                  href="/forgot-password" 
                  className="text-sm text-[#ff4800] hover:text-[#ff4800]/80 transition-colors"
                >
                  Forgot your password?
                </a>
              </div>

              {error && (
                <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg text-red-500 text-sm">
                  {error}
                  {showResendVerification && (
                    <div className="mt-3">
                      <button
                        type="button"
                        onClick={handleResendVerification}
                        disabled={resendLoading}
                        className="text-sm text-[#ff4800] hover:text-[#ff4800]/80 transition-colors underline disabled:opacity-50"
                      >
                        {resendLoading ? 'Sending...' : 'Resend verification email'}
                      </button>
                    </div>
                  )}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full py-2 px-4 bg-[#ff4800] hover:bg-[#ff4800]/90 text-white text-sm rounded-md transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading && !showWorkspaceLoading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white/20 border-t-white mr-2"></div>
                    Signing in...
                  </div>
                ) : (
                  'Sign in'
                )}
              </button>
            </form>

            <div className="relative mt-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-border"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-background text-muted-foreground">Or continue with</span>
              </div>
            </div>

            <div className="mt-4 flex flex-col gap-3 w-full">
              <OAuthButton provider="google" />
              {/* <OAuthButton provider="microsoft" /> */}
              <OAuthButton provider="github" />
            </div>
          </div>

          <div className="mt-6 text-center text-sm text-muted-foreground">
            Don't have an account?{' '}
            <a href="/register" className="text-[#ff4800] hover:text-[#ff4800]/80 transition-colors">
              Sign up
            </a>
          </div>

          <div className="mt-6 text-center text-sm text-muted-foreground">
            Built with ❤️ by <a href="https://www.openana.ai/" className="text-[#ff4800] hover:text-[#ff4800]/80 transition-colors dark:text-[#ff6933] dark:hover:text-[#ff6933]/80" target="_blank" rel="noopener noreferrer">Ana</a>
          </div>
        </div>
      </div>
    </>
  );
};

export default Login;
